import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Tag } from "lucide-react";

import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/motion/Reveal";
import MarkdownLite from "@/components/MarkdownLite";
import CTASection from "@/components/CTASection";
import { fetchArticle, fetchArticles } from "@/lib/api";

export default function InsightDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const ins = t.insights;
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    fetchArticle(slug)
      .then((a) => {
        setArticle(a);
        document.title = `${a.title.en} — Litterantara`;
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute("content", a.excerpt.en);
      })
      .catch(() => setNotFound(true));
    fetchArticles().then((all) => setRelated(all.filter((x) => x.slug !== slug).slice(0, 3)));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-navy px-6">
        <p className="font-display text-2xl text-[#F0F4F8]">404</p>
        <button
          onClick={() => navigate("/insights")}
          className="font-mono-tech text-[12px] uppercase tracking-widest text-cyan-precision border border-cyan-precision/40 px-6 py-3"
        >
          {ins.back}
        </button>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy">
        <span className="font-mono-tech text-[12px] uppercase tracking-widest text-cyan-precision animate-pulse">…</span>
      </div>
    );
  }

  return (
    <div data-testid="insight-detail-page">
      {/* Hero */}
      <section className="relative pt-36 pb-10 overflow-hidden">
        <div className="max-w-[820px] mx-auto px-6 lg:px-10">
          <Link
            to="/insights"
            data-testid="insight-back"
            className="inline-flex items-center gap-1.5 font-mono-tech text-[11px] uppercase tracking-widest text-[#94A3B8] hover:text-cyan-precision transition-colors mb-8"
          >
            <ArrowLeft size={13} /> {ins.back}
          </Link>
          <div className="flex flex-wrap items-center gap-4 font-mono-tech text-[11px] uppercase tracking-widest text-cyan-precision">
            <span>{article.category}</span>
            <span className="text-[#94A3B8]/40">·</span>
            <span className="flex items-center gap-1.5 text-[#94A3B8]">
              <Clock size={12} /> {article.read_minutes} {ins.minRead}
            </span>
            <span className="text-[#94A3B8]/40">·</span>
            <span className="text-[#94A3B8]">{article.date}</span>
          </div>
          <h1 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F0F4F8] leading-[1.1]">
            {article.title[lang]}
          </h1>
          <p className="mt-5 text-lg text-[#94A3B8] leading-relaxed">{article.excerpt[lang]}</p>
          <p className="mt-6 font-mono-tech text-[11px] uppercase tracking-widest text-[#94A3B8]">
            {ins.by} {article.author}
          </p>
        </div>
      </section>

      {/* Cover */}
      <Reveal>
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 mb-12">
          <div className="overflow-hidden border border-white/10">
            <img src={article.cover} alt={article.title[lang]} className="w-full h-[300px] lg:h-[440px] object-cover" />
          </div>
        </div>
      </Reveal>

      {/* Body */}
      <article className="max-w-[820px] mx-auto px-6 lg:px-10 pb-12">
        <MarkdownLite text={article.body[lang]} />

        {article.tags?.length > 0 && (
          <div className="mt-12 pt-8 border-t border-white/10 flex flex-wrap items-center gap-2">
            <Tag size={14} className="text-cyan-precision" />
            {article.tags.map((tg) => (
              <span
                key={tg}
                className="font-mono-tech text-[11px] uppercase tracking-widest border border-white/15 text-[#F0F4F8]/80 px-3 py-1.5"
              >
                {tg}
              </span>
            ))}
          </div>
        )}
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-[1320px] mx-auto px-6 lg:px-10 pb-8">
          <h3 className="font-mono-tech text-[12px] uppercase tracking-widest text-[#94A3B8] mb-6">{ins.related}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((a) => (
              <Link
                key={a.slug}
                to={`/insights/${a.slug}`}
                className="group border border-white/10 bg-navy-surface/40 overflow-hidden hover:border-cyan-precision/40 transition-colors duration-300"
              >
                <div className="h-36 overflow-hidden">
                  <img src={a.cover} alt={a.title[lang]} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <span className="font-mono-tech text-[10px] uppercase tracking-widest text-cyan-precision">{a.category}</span>
                  <h4 className="mt-2 font-display text-lg font-light text-[#F0F4F8] leading-snug">{a.title[lang]}</h4>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <CTASection title={ins.ctaTitle} body={ins.ctaBody} btn={ins.ctaBtn} />
    </div>
  );
}
